import { useState, useEffect } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { Button, Input } from '@theraply/ui';
import {
  apiClient,
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  type UpdatePreferencesRequest,
} from '@theraply/api-client';

type SettingsTab = 'profile' | 'notifications' | 'security';

interface ProfileData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  dateOfBirth?: string;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

export default function SettingsPage() {
  const { user } = useAuthContext();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');

  const tabs: { key: SettingsTab; label: string }[] = [
    { key: 'profile', label: 'Profile' },
    { key: 'notifications', label: 'Notifications' },
    { key: 'security', label: 'Security' },
  ];

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
      <p className="mt-1 text-sm text-gray-500">
        Manage your account and application settings.
      </p>

      {/* Tabs */}
      <div className="mt-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-6">
        {activeTab === 'profile' && <ProfileSection />}
        {activeTab === 'notifications' && <NotificationsSection />}
        {activeTab === 'security' && <SecuritySection />}
      </div>
    </div>
  );
}

function ProfileSection() {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    dateOfBirth: '',
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiClient.get('/consumers/me');
      const data = response.data;
      setProfile(data);
      setFormData({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        phone: data.phone || '',
        dateOfBirth: data.dateOfBirth || '',
      });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await apiClient.put('/consumers/me', {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone || null,
        dateOfBirth: formData.dateOfBirth || null,
      });
      setSuccessMessage('Profile updated successfully');
      setIsEditing(false);
      await fetchProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        phone: profile.phone || '',
        dateOfBirth: profile.dateOfBirth || '',
      });
    }
    setIsEditing(false);
    setError(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load profile.</p>
        <button
          onClick={fetchProfile}
          className="mt-2 text-sm text-red-600 underline hover:text-red-800"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-5 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Personal Information</h2>
          <p className="mt-1 text-sm text-gray-500">
            Update your personal details and contact information.
          </p>
        </div>
        <div className="px-6 py-6">
          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          {successMessage && (
            <div className="mb-4 rounded-md bg-green-50 p-4">
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="flex items-center">
                <Input type="email" value={profile.email} disabled fullWidth />
                {profile.emailVerified && (
                  <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Verified
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                id="firstName"
                name="firstName"
                type="text"
                label="First Name"
                value={formData.firstName}
                onChange={handleChange}
                disabled={!isEditing}
                required
                fullWidth
              />
              <Input
                id="lastName"
                name="lastName"
                type="text"
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                disabled={!isEditing}
                required
                fullWidth
              />
            </div>

            <Input
              id="phone"
              name="phone"
              type="tel"
              label="Phone Number"
              value={formData.phone}
              onChange={handleChange}
              disabled={!isEditing}
              placeholder="+1 (555) 123-4567"
              fullWidth
            />

            <Input
              id="dateOfBirth"
              name="dateOfBirth"
              type="date"
              label="Date of Birth"
              value={formData.dateOfBirth}
              onChange={handleChange}
              disabled={!isEditing}
              fullWidth
            />

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              {!isEditing ? (
                <Button type="button" onClick={() => setIsEditing(true)}>
                  Edit Profile
                </Button>
              ) : (
                <>
                  <Button type="button" variant="outline" onClick={handleCancel} disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button type="submit" isLoading={isSaving}>
                    Save Changes
                  </Button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function NotificationsSection() {
  const { data: preferences, isLoading, error, refetch } = useNotificationPreferences();
  const updateMutation = useUpdateNotificationPreferences();

  const [localPrefs, setLocalPrefs] = useState<UpdatePreferencesRequest>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (preferences) {
      setLocalPrefs({
        emailEnabled: preferences.emailEnabled,
        smsEnabled: preferences.smsEnabled,
        reminder24h: preferences.reminder24h,
        reminder1h: preferences.reminder1h,
      });
    }
  }, [preferences]);

  useEffect(() => {
    if (preferences) {
      const changed =
        localPrefs.emailEnabled !== preferences.emailEnabled ||
        localPrefs.smsEnabled !== preferences.smsEnabled ||
        localPrefs.reminder24h !== preferences.reminder24h ||
        localPrefs.reminder1h !== preferences.reminder1h;
      setHasChanges(changed);
      if (changed) setSaveSuccess(false);
    }
  }, [localPrefs, preferences]);

  const handleToggle = (field: keyof UpdatePreferencesRequest) => {
    setLocalPrefs((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync(localPrefs);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save preferences:', err);
    }
  };

  const handleReset = () => {
    if (preferences) {
      setLocalPrefs({
        emailEnabled: preferences.emailEnabled,
        smsEnabled: preferences.smsEnabled,
        reminder24h: preferences.reminder24h,
        reminder1h: preferences.reminder1h,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading notification preferences...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load notification preferences.</p>
        <button
          onClick={() => refetch()}
          className="mt-2 text-sm text-red-600 underline hover:text-red-800"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      {saveSuccess && (
        <div className="rounded-md bg-green-50 p-4">
          <p className="text-sm text-green-800">
            Notification preferences saved successfully.
          </p>
        </div>
      )}

      {updateMutation.isError && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">Failed to save preferences. Please try again.</p>
        </div>
      )}

      {/* Notification Channels */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-5 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Notification Channels</h2>
          <p className="mt-1 text-sm text-gray-500">Choose how you receive notifications.</p>
        </div>
        <div className="px-6 py-4 space-y-4">
          <ToggleRow
            label="Email Notifications"
            description="Receive notifications via email"
            checked={localPrefs.emailEnabled ?? true}
            onChange={() => handleToggle('emailEnabled')}
          />
          <ToggleRow
            label="SMS Notifications"
            description="Receive text messages for important updates"
            checked={localPrefs.smsEnabled ?? true}
            onChange={() => handleToggle('smsEnabled')}
          />
        </div>
      </div>

      {/* Session Reminders */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-5 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Session Reminders</h2>
          <p className="mt-1 text-sm text-gray-500">Choose when you want to be reminded about upcoming sessions.</p>
        </div>
        <div className="px-6 py-4 space-y-4">
          <ToggleRow
            label="24-Hour Reminder"
            description="Get reminded one day before your session"
            checked={localPrefs.reminder24h ?? true}
            onChange={() => handleToggle('reminder24h')}
            disabled={!localPrefs.emailEnabled && !localPrefs.smsEnabled}
          />
          <ToggleRow
            label="1-Hour Reminder"
            description="Get reminded one hour before your session starts"
            checked={localPrefs.reminder1h ?? true}
            onChange={() => handleToggle('reminder1h')}
            disabled={!localPrefs.emailEnabled && !localPrefs.smsEnabled}
          />
        </div>

        {!localPrefs.emailEnabled && !localPrefs.smsEnabled && (
          <div className="px-6 pb-4">
            <div className="rounded-md bg-blue-50 p-3">
              <p className="text-sm text-blue-800">
                Enable at least one notification channel to receive reminders.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={handleReset} disabled={!hasChanges || updateMutation.isPending}>
          Reset
        </Button>
        <Button onClick={handleSave} disabled={!hasChanges || updateMutation.isPending} isLoading={updateMutation.isPending}>
          Save Changes
        </Button>
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className={`text-sm font-medium ${disabled ? 'text-gray-400' : 'text-gray-900'}`}>
          {label}
        </p>
        <p className={`text-sm ${disabled ? 'text-gray-300' : 'text-gray-500'}`}>
          {description}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${
          disabled
            ? 'cursor-not-allowed opacity-50'
            : ''
        } ${checked ? 'bg-indigo-600' : 'bg-gray-200'}`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

function SecuritySection() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChanging, setIsChanging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long.');
      return;
    }

    try {
      setIsChanging(true);
      await apiClient.post('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      setSuccessMessage('Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(
        err.response?.data?.message || err.message || 'Failed to change password.'
      );
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-5 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Change Password</h2>
          <p className="mt-1 text-sm text-gray-500">
            Update your password to keep your account secure.
          </p>
        </div>
        <div className="px-6 py-6">
          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          {successMessage && (
            <div className="mb-4 rounded-md bg-green-50 p-4">
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
          )}

          <form onSubmit={handleChangePassword} className="space-y-4">
            <Input
              id="currentPassword"
              type="password"
              label="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              fullWidth
            />
            <Input
              id="newPassword"
              type="password"
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              fullWidth
            />
            <Input
              id="confirmPassword"
              type="password"
              label="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              fullWidth
            />
            <div className="flex justify-end pt-4 border-t border-gray-200">
              <Button type="submit" isLoading={isChanging}>
                Change Password
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
